import React, { useState } from 'react';

// Import required components (assuming they exist in your project structure)
import Sidebar from '../Sidebar';
import LogoutButton from '../Login/LogoutButton';

const JobCreator = () => {
    const [mode, setMode] = useState('manual'); // 'manual' or 'description'
    const [jobTitle, setJobTitle] = useState('');
    const [jobDescription, setJobDescription] = useState('');
    const [exclusionCriteria, setExclusionCriteria] = useState('');
    const [requiredSkills, setRequiredSkills] = useState('');
    const [experienceRequired, setExperienceRequired] = useState('');
    const [additionalRequirements, setAdditionalRequirements] = useState('');
    
    const [isProcessing, setIsProcessing] = useState(false);
    const [response, setResponse] = useState(null);
    const [error, setError] = useState('');

    const createJob = async () => {
        // Validation based on mode
        if (mode === 'manual' && !jobTitle.trim()) {
            setError('Job title is required for manual mode');
            return;
        }
        
        if (mode === 'description' && (!jobTitle.trim() || !jobDescription.trim())) {
            setError('Both job title and job description are required for AI analysis mode');
            return;
        }

        setIsProcessing(true);
        setError('');
        setResponse(null);

        try {
            let requestBody;
            
            if (mode === 'description') {
                // AI Analysis Mode
                requestBody = {
                    useJobDescription: true,
                    jobTitle: jobTitle.trim(),
                    fullJobDescription: jobDescription.trim(),
                    exclusionCriteria: exclusionCriteria.trim() || null,
                    createdBy: 'admin@company.com'
                };
            } else {
                // Manual Mode
                requestBody = {
                    useJobDescription: false,
                    jobTitle: jobTitle.trim(),
                    requiredSkills: requiredSkills.trim() || null,
                    experienceRequired: experienceRequired.trim() || null,
                    additionalRequirements: additionalRequirements.trim() || null,
                    createdBy: 'admin@company.com'
                };
            }

           // console.log('Sending request:', requestBody);

            const apiResponse = await fetch('https://n8n.acespireconsulting.com/webhook/admin/jobs', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody)
            });

            if (!apiResponse.ok) {
                throw new Error(`HTTP error! status: ${apiResponse.status}`);
            }

            const result = await apiResponse.json();
            //console.log('API Response:', result);
            
            setResponse(result);
            
            // Clear form on success
            setJobTitle('');
            setJobDescription('');
            setExclusionCriteria('');
            setRequiredSkills('');
            setExperienceRequired('');
            setAdditionalRequirements('');
            
        } catch (err) {
            console.error('Error creating job:', err);
            setError(`Failed to create job: ${err.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div className="flex min-h-screen" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {/* Sidebar */}
            <Sidebar />

            {/* Main Content */}
            <div className="flex-1 bg-white overflow-hidden">
                {/* Header */}
                <div className="p-8 border-b border-gray-200 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <h1 className="text-2xl font-semibold text-gray-800">Hireon AI</h1>
                    </div>
                    <LogoutButton />
                </div>

                {/* Content */}
                <div className="p-8 flex flex-col items-center justify-center min-h-[600px]">
                    <div className="w-full max-w-4xl text-center">
                        <h2 className="text-4xl font-bold text-gray-800 mb-12">Let's Create A Job Criteria</h2>

                        {/* Mode Selection Toggle */}
                        <div className="mb-8">
                            <div className="flex justify-center items-center gap-4 mb-6">
                                <button
                                    onClick={() => setMode('manual')}
                                    className={`px-6 py-3 rounded-lg font-medium transition-all ${
                                        mode === 'manual'
                                            ? 'bg-[#275F97] text-white shadow-lg'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                                    disabled={isProcessing}
                                >
                                    Manual Entry
                                </button>
                                <div className="text-gray-400 font-medium">OR</div>
                                <button
                                    onClick={() => setMode('description')}
                                    className={`px-6 py-3 rounded-lg font-medium transition-all ${
                                        mode === 'description'
                                            ? 'bg-[#275F97] text-white shadow-lg'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                                    disabled={isProcessing}
                                >
                                    AI Analysis
                                </button>
                            </div>
                        </div>

                        {/* Form Fields */}
                        <div className={isProcessing ? 'opacity-50 pointer-events-none' : ''}>
                            <div className="space-y-8">
                                
                                {/* Job Title - Always visible */}
                                <div>
                                    <input
                                        type="text"
                                        placeholder="Job Title (e.g., Senior Software Engineer)"
                                        value={jobTitle}
                                        onChange={(e) => setJobTitle(e.target.value)}
                                        className="w-full p-4 border-2 border-gray-200 rounded-xl text-lg outline-none focus:border-gray-400 transition-all"
                                        disabled={isProcessing}
                                    />
                                </div>

                                {/* Manual Mode Fields */}
                                {mode === 'manual' && (
                                    <>
                                        <div>
                                            <input
                                                type="text"
                                                placeholder="Required Skills (comma separated, e.g., JavaScript, React, Node.js)"
                                                value={requiredSkills}
                                                onChange={(e) => setRequiredSkills(e.target.value)}
                                                className="w-full p-4 border-2 border-gray-200 rounded-xl text-lg outline-none focus:border-gray-400 transition-all"
                                                disabled={isProcessing}
                                            />
                                        </div>
                                        
                                        <div>
                                            <input
                                                type="text"
                                                placeholder="Experience Required (e.g., 3-5 years)"
                                                value={experienceRequired}
                                                onChange={(e) => setExperienceRequired(e.target.value)}
                                                className="w-full p-4 border-2 border-gray-200 rounded-xl text-lg outline-none focus:border-gray-400 transition-all"
                                                disabled={isProcessing}
                                            />
                                        </div>
                                        
                                        <div>
                                            <textarea
                                                placeholder="Additional Requirements (Optional)"
                                                value={additionalRequirements}
                                                onChange={(e) => setAdditionalRequirements(e.target.value)}
                                                className="w-full p-4 border-2 border-gray-200 rounded-xl text-lg outline-none focus:border-gray-400 transition-all min-h-[120px] resize-vertical"
                                                disabled={isProcessing}
                                            />
                                        </div>
                                    </>
                                )}

                                {/* AI Analysis Mode Fields */}
                                {mode === 'description' && (
                                    <>
                                        <div>
                                            <textarea
                                                placeholder="Paste your complete job description here..."
                                                value={jobDescription}
                                                onChange={(e) => setJobDescription(e.target.value)}
                                                className="w-full p-4 border-2 border-gray-200 rounded-xl text-lg outline-none focus:border-gray-400 transition-all min-h-[200px] resize-vertical font-mono text-sm"
                                                disabled={isProcessing}
                                            />
                                            {/* <div className="text-right text-sm text-gray-500 mt-2">
                                                {jobDescription.length} characters
                                            </div> */}
                                        </div>
                                        
                                        <div>
                                            <input
                                                type="text"
                                                placeholder="Exclusion Criteria (comma separated, e.g., O9 Solutions, Oracle EBS)"
                                                value={exclusionCriteria}
                                                onChange={(e) => setExclusionCriteria(e.target.value)}
                                                className="w-full p-4 border-2 border-gray-200 rounded-xl text-lg outline-none focus:border-gray-400 transition-all"
                                                disabled={isProcessing}
                                            />
                                            {exclusionCriteria && (
                                                <div className="text-sm text-orange-600 mt-2">
                                                    {exclusionCriteria.split(',').length} exclusion criteria
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}

                                {/* Error Display */}
                                {error && (
                                    <div className="mb-6 p-4 rounded-lg border-2 bg-red-50 border-red-200">
                                        <div className="text-red-600 font-medium text-center">
                                            {error}
                                        </div>
                                    </div>
                                )}

                                {/* Submit Button */}
                                <button
                                    onClick={createJob}
                                    disabled={isProcessing || 
                                        (mode === 'manual' && !jobTitle.trim()) || 
                                        (mode === 'description' && (!jobTitle.trim() || !jobDescription.trim()))}
                                    className={`flex mx-auto items-center gap-3 px-12 py-4 rounded-[40px] font-semibold text-lg transition-all ${
                                        isProcessing || 
                                        (mode === 'manual' && !jobTitle.trim()) || 
                                        (mode === 'description' && (!jobTitle.trim() || !jobDescription.trim()))
                                            ? 'bg-[#275F97] text-white cursor-not-allowed'
                                            : 'bg-[#275F97] text-white hover:shadow-lg hover:bg-[#1e4a78]'
                                    }`}
                                >
                                    {isProcessing ? (
                                        <>
                                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            {mode === 'description' ? 'Analyzing Job Description...' : 'Creating Job...'}
                                        </>
                                    ) : (
                                        <>
                                            <span>{mode === 'description' ? 'Analyze & Create Job' : 'Create Job'}</span>
                                        </>
                                    )}
                                </button>

                                {/* Success Response */}
                                {response && (
                                    <div className="mt-8 p-6 rounded-lg border-2 bg-green-50 border-green-200 max-w-2xl mx-auto">
                                        <h3 className="font-semibold text-green-800 mb-4 flex items-center justify-center gap-2 text-lg">
                                            Job Created Successfully!
                                        </h3>
                                        <div className="text-sm text-green-700 space-y-2 text-left">
                                            <p><strong>Job ID:</strong> {response.id || 'N/A'}</p>
                                            <p><strong>Title:</strong> {response.job_title || jobTitle}</p>
                                            <p><strong>Mode:</strong> {response.use_job_description ? 'AI Analysis' : 'Manual'}</p>
                                            <p><strong>Status:</strong> {response.message || 'Created'}</p>
                                            {response.analysis_completed && (
                                                <p><strong>Analysis:</strong> {response.analysis_completed ? 'Completed' : 'Pending'}</p>
                                            )}
                                            {response.skills_count && (
                                                <p><strong>Skills Extracted:</strong> {response.skills_count}</p>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default JobCreator;